import { useState, useEffect } from 'react'
import { useWebSocket } from './useWebSocket'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { FraudAlertData } from '@/services/websocket'

export interface RealTimeAlert extends FraudAlertData {
  // Additional UI state
  isRead?: boolean
  isDismissed?: boolean
}

export function useRealTimeAlerts() {
  const [alerts, setAlerts] = useState<RealTimeAlert[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const queryClient = useQueryClient()
  
  const { subscribe, isConnected } = useWebSocket({
    events: [
      {
        type: 'fraud_alert',
        handler: (data: FraudAlertData) => {
          // Add to alerts list
          const newAlert: RealTimeAlert = {
            ...data,
            isRead: false,
            isDismissed: false
          }
          
          setAlerts(prev => [newAlert, ...prev].slice(0, 50)) // Keep last 50
          setUnreadCount(prev => prev + 1)
          
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
          queryClient.invalidateQueries({ queryKey: ['metrics'] })
          
          // Show notification based on risk level
          if (data.risk_level === 'critical') {
            toast.error(
              `🚨 Critical fraud detected! Lead #${data.lead_id.slice(-6)}`,
              {
                description: `Risk Score: ${data.risk_score}% | Patterns: ${data.patterns_detected.join(', ')}`,
                duration: 10000,
                action: {
                  label: 'View Details',
                  onClick: () => {
                    // Navigate to leads page with filter
                    window.location.href = `/leads?search=${data.lead_id}`
                  }
                }
              }
            )
          } else if (data.risk_level === 'high') {
            toast.warning(
              `⚠️ High-risk activity detected: ${data.patterns_detected[0]}`,
              {
                description: `Risk Score: ${data.risk_score}% | Lead #${data.lead_id.slice(-6)}`,
                duration: 7000
              }
            )
          } else if (data.risk_level === 'medium') {
            toast.info(
              `Medium-risk pattern detected`,
              {
                description: `${data.patterns_detected.join(', ')} | Score: ${data.risk_score}%`,
                duration: 5000
              }
            )
          }
        }
      },
      {
        type: 'metrics_update',
        handler: () => {
          // Refresh dashboard metrics
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
          queryClient.invalidateQueries({ queryKey: ['metrics'] })
        }
      }
    ]
  })
  
  // Mark alerts as read
  const markAsRead = (alertIds?: string[]) => {
    if (!alertIds) {
      // Mark all as read
      setAlerts(prev => prev.map(alert => ({ ...alert, isRead: true })))
      setUnreadCount(0)
    } else {
      // Mark specific alerts as read
      setAlerts(prev => prev.map(alert => 
        alertIds.includes(alert.id) 
          ? { ...alert, isRead: true }
          : alert
      ))
      setUnreadCount(prev => Math.max(0, prev - alertIds.length))
    }
  }
  
  // Clear all alerts
  const clearAlerts = () => {
    setAlerts([])
    setUnreadCount(0)
  }
  
  // Dismiss specific alert
  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId 
        ? { ...alert, isDismissed: true }
        : alert
    ))
    
    // Remove from unread count if it was unread
    setAlerts(prev => {
      const alert = prev.find(a => a.id === alertId)
      if (alert && !alert.isRead) {
        setUnreadCount(count => Math.max(0, count - 1))
      }
      return prev
    })
    
    // Actually remove after animation
    setTimeout(() => {
      setAlerts(prev => prev.filter(alert => alert.id !== alertId))
    }, 300)
  }
  
  // Get active (non-dismissed) alerts
  const activeAlerts = alerts.filter(alert => !alert.isDismissed)
  
  // Get critical alerts count
  const criticalAlerts = activeAlerts.filter(alert => alert.risk_level === 'critical')
  
  // Get high-risk alerts count
  const highRiskAlerts = activeAlerts.filter(alert => 
    alert.risk_level === 'high' || alert.risk_level === 'critical'
  )
  
  // Auto-mark alerts as read after 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setAlerts(prev => prev.map(alert => {
        const alertAge = Date.now() - new Date(alert.created_at).getTime()
        if (!alert.isRead && alertAge > 30000) { // 30 seconds
          setUnreadCount(count => Math.max(0, count - 1))
          return { ...alert, isRead: true }
        }
        return alert
      }))
    }, 5000)
    
    return () => clearInterval(timer)
  }, [])
  
  return {
    alerts: activeAlerts,
    unreadCount,
    isConnected,
    criticalAlerts: criticalAlerts.length,
    highRiskAlerts: highRiskAlerts.length,
    markAsRead,
    clearAlerts,
    dismissAlert
  }
}